import React from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from 'src/components/UI/table/Table';
import {
  AppContext,
  LoadingContext,
  SnackContext
} from 'src/store/ContextStore';
import {
  useQuery,
  useQueryClient,
  keepPreviousData
} from '@tanstack/react-query';
import { capitalizeLetter } from 'src/util/util';
import CustomIconBtn from 'src/components/UI/button/CustomIconBtn';
import { toLabel } from 'src/util/util';
import useDateFormatter from 'src/hooks/useDateFormatter';
import {
  APICONSTANTS,
  CONSTANTS,
  TABLE_CONSTANTS
} from 'src/components/Constants';
import axios from 'src/axios';
import { EVENTS, displayableGenotypes } from 'src/components/admin/appReducer';
import {
  getTableStateFor,
  saveTableState,
  clampPageToTotal
} from 'src/components/UI/table/tableStatePersistence';
import {
  diagnosesMap,
  relationshipMap
} from 'src/components/questionnaire/Constants';
import { resolveTagStyle } from 'src/util/tagChipStyle';
import UsersTableStatusCol from './UsersTableStatusCol';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { Visibility } from '@mui/icons-material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PersonSearchPicker from '../merge-person/PersonSearchPicker';
import MergePersonSelectModal from '../merge-person/MergePersonSelectModal';
import MergePersonWizard from '../merge-person/MergePersonWizard';
import FamilySearchFilterBar from './FamilySearchFilters/FamilySearchFilterBar';
import FamilySearchResultLine from './FamilySearchFilters/FamilySearchResultLine';
import useFamilySearchFilters from './FamilySearchFilters/useFamilySearchFilters';
import { applyCriteria } from './FamilySearchFilters/filterPredicates';
import { suggestIdentifierCriterion } from './FamilySearchFilters/identifierPatterns';
import { criterionSentence } from './FamilySearchFilters/filterLabels';
import usePedigreeFieldLabels from 'src/hooks/usePedigreeFieldLabels';
import { formatPartialDate, PRECISION } from 'src/util/partialDate';

// Browse pagination is persisted under its own key, separate from the Table
// component's shared table state — see the note by the pagination state below.
const BROWSE_STATE_KEY = `${TABLE_CONSTANTS.familyMembers}:browse`;

/**
 * Render a stored date honestly, at the precision it was captured.
 *
 * A year-only date of birth is STORED as '1915-01-01' — partialDate.js pads the
 * missing components so the value stays byte-compatible with the desktop XML
 * export. Handing that straight to fmtDate prints "01/01/1915", which asserts a
 * day the record does not have. TGCLOUD-1818 projects `dobPrecision` /
 * `estimatedDob` (and the dod pair) so the grid can say "1915" instead.
 *
 * `estimated*` is the legacy fallback, read only when precision is absent —
 * partialDate.js treats an estimated flag with no precision as year precision.
 *
 * Pure, and takes the formatter as a parameter rather than calling
 * useDateFormatter itself: a private formatter inside a helper is how two
 * hardcoded-locale defects got in (CLAUDE.md).
 */
const renderStoredDate = (value, precision, estimated, fmtDate) => {
  if (!value) return '';
  const effective = precision || (estimated ? PRECISION.YEAR : PRECISION.FULL);
  if (effective === PRECISION.FULL) return fmtDate(value);
  const match = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!match) return fmtDate(value);
  const [, year, month] = match;
  return formatPartialDate({
    year: Number(year),
    month: Number(month),
    day: null,
    precision: effective
  });
};

const makeDateQuickFilter = (fmtDate) => (search) => {
  const needle = String(search ?? '')
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (String(value).toLowerCase().includes(needle)) return true;
    try {
      return String(fmtDate(value) ?? '')
        .toLowerCase()
        .includes(needle);
    } catch {
      return false;
    }
  };
};

export default function UsersTableInfo({
  // openDeleteModal and markAsImported are still passed by UsersTable.jsx but
  // have had no consumer since the `columns` memo that used them went dead;
  // removing the dead memo in TGCLOUD-1818 only made that visible. Left in the
  // signature rather than dropped, because the parent still renders the modals
  // they open and deciding whether those actions should come back belongs with
  // whoever owns this screen, not with a search-filter ticket.
  // eslint-disable-next-line no-unused-vars
  openDeleteModal,
  patient,
  // eslint-disable-next-line no-unused-vars
  markAsImported,
  focusSearch
}) {
  const t = useAutoTranslation();

  const navigate = useNavigate();
  const fmtDate = useDateFormatter();
  // Ref to the wrapper's search input — replaces the old MRT-internals reach
  // (tableRef.current.refs.globalFilterInputRef) for the focusSearch feature.
  const searchInputRef = React.useRef(null);
  const { setLoading } = React.useContext(LoadingContext);
  const { setSnack } = React.useContext(SnackContext);
  const {
    content: { probandUsers: _proband_users },
    dispatchUpdate
  } = React.useContext(AppContext);

  const queryClient = useQueryClient();
  const highlightMatch = (label, query) => {
    if (!query) return label;
    const i = label.toLowerCase().indexOf(query);
    if (i === -1) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark>{label.slice(i, i + query.length)}</mark>
        {label.slice(i + query.length)}
      </>
    );
  };

  // Chips + free text live together and mirror into the URL. The free-text box
  // behaves exactly as before — this only changes WHERE the value is held.
  const {
    criteria,
    searchText: globalFilter,
    addCriterion,
    updateCriterion,
    removeCriterion,
    setSearchText: setGlobalFilter,
    clearAll,
    pruneCriteria
  } = useFamilySearchFilters();

  // The served field catalogue. Cached hard: it only changes when the
  // encryption map does, which is a deploy, not a session.
  const { data: filterFields, isError: filterFieldsError } = useQuery({
    queryKey: ['personFilterFields'],
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async () => {
      const res = await axios.get(APICONSTANTS.getPersonFilterFields);
      return res.data || [];
    }
  });
  const fields = React.useMemo(() => filterFields ?? [], [filterFields]);
  // The catalogue is what maps a criterion's field key to a TYPE, and without
  // a type there is no predicate. So a criterion restored from the URL before
  // the catalogue lands would match nothing to filter ON, and applyCriteria
  // would hand back every row — chips on screen, result line confidently
  // reporting the whole tenant, nothing actually narrowed. The index request
  // is far larger and so almost always lands second, which is exactly what
  // would make this rare enough to ship and confusing enough to matter.
  const fieldsReady = Array.isArray(filterFields);
  // If the catalogue cannot be fetched at all, the chips cannot be honoured.
  // Saying so and showing nothing is the right direction to fail here: the
  // alternative — dropping the predicates and rendering the unfiltered tenant
  // under active chips — claims a narrowed result set that was never applied.
  const filtersUnavailable = criteria.length > 0 && filterFieldsError;

  // A tenant may have renamed Hospital Number / Healthcare ID / Mobile. The
  // hook is shared and cached, so this costs no extra request.
  const tenantFieldLabels = usePedigreeFieldLabels();

  // `queryForField(field)` used to return '' when the "All fields" picker had
  // un-ticked that column, so the <mark> highlight matched what the search was
  // actually scoped to. With the picker gone (TGCLOUD-1818) every searchable
  // column is in scope again, so the highlight query is simply the term.
  // Keeping the highlighting matters: dropping it with the picker would have
  // silently removed the <mark>s from the Disease/Phenotype/Genotype cells.
  const highlightQuery = React.useMemo(
    () => (globalFilter || '').trim().toLowerCase(),
    [globalFilter]
  );
  const dateQuickFilter = React.useMemo(
    () => makeDateQuickFilter(fmtDate),
    [fmtDate]
  );

  // Server-paginated tables cannot use Table's own page persistence: it only
  // restores a saved page when the consumer passes no `page` prop, and a
  // controlled server table always passes one. So the page survived navigation
  // for every client-side table (Pedigrees et al) but never for this one.
  //
  // The parent owns the page here, so the parent owns restoring it. It also gets
  // its OWN key rather than sharing Table's: while a search is active this table
  // switches to client pagination with a hardcoded pageSize of 15, so the shared
  // key would hand browse mode a search-result page number and clobber a chosen
  // page size.
  const [pagination, setPagination] = React.useState(() => {
    const saved = getTableStateFor(BROWSE_STATE_KEY);
    return {
      pageIndex: saved?.page ?? 0,
      pageSize: saved?.pageSize ?? 15
    };
  });
  const searchActive = (globalFilter || '').trim().length > 0;

  // A user sort. Tracked because browse mode is SERVER-paginated: the browser
  // holds one page, so sorting it reorders 15 rows while the footer still
  // reports the whole tenant. That was true before TGCLOUD-1818 and is a real
  // defect; the fix is to treat a sort like a search and widen to the full
  // index, which is the same switch chips use.
  const [sortActive, setSortActive] = React.useState(false);

  // THE RULE, in one place: anything that narrows or reorders the result set
  // needs the WHOLE set, not the current page. Getting this wrong is the one
  // way this feature returns fewer people than exist while claiming more —
  // so it is a single named boolean rather than three scattered conditions.
  const fullIndexActive = searchActive || criteria.length > 0 || sortActive;

  // Persist only while browsing — search paging is transient and belongs to the
  // search results, not to the position we return the user to.
  React.useEffect(() => {
    if (fullIndexActive) return;
    saveTableState(BROWSE_STATE_KEY, {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize
    });
  }, [fullIndexActive, pagination.pageIndex, pagination.pageSize]);

  const [enableIndexFetch, setEnableIndexFetch] = React.useState(false);
  React.useEffect(() => {
    if (!fullIndexActive) {
      setEnableIndexFetch(false);
      return undefined;
    }
    // The 500ms arming delay is for TYPING — it stops a full-tenant download
    // firing on the first keystroke of a word. A chip or a sort is a discrete,
    // deliberate act with nothing more coming, so it fetches immediately.
    if (!searchActive) {
      setEnableIndexFetch(true);
      return undefined;
    }
    const id = setTimeout(() => setEnableIndexFetch(true), 500);
    return () => clearTimeout(id);
  }, [fullIndexActive, searchActive, globalFilter]);

  const {
    data: browseData,
    isLoading: browseLoading,
    isFetching: browseFetching,
    isError: browseError
  } = useQuery({
    queryKey: ['probandBrowse', pagination.pageIndex, pagination.pageSize],
    // React Query v5 REMOVED the `keepPreviousData: true` option — it was
    // silently ignored here, so every page change blanked `browseData` while the
    // next page loaded. That made `totalCount` fall to 0, the grid decided the
    // current page was out of range and emitted page 0, and the user was thrown
    // back to the first page mid-browse. The v5 spelling actually keeps the
    // previous page's rows AND its totalCount on screen during the fetch.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await axios.get(
        `${APICONSTANTS.getProbandBrowse}?page=${pagination.pageIndex}&limit=${pagination.pageSize}`
      );
      return res.data || { data: [], totalCount: 0 };
    }
  });

  const {
    data: indexData,
    isFetching: indexFetching,
    isError: indexError
  } = useQuery({
    queryKey: ['familySearchIndex'],
    enabled: enableIndexFetch,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const res = await axios.get(APICONSTANTS.getProbandSearchIndex);
      return res.data || [];
    }
  });

  // A page restored from a previous session can point past the end of the data.
  // Guarded on a real, non-zero totalCount so it can never fire during the
  // request-in-flight window — correcting the page there is the very bug that
  // was throwing the user back to page 1.
  React.useEffect(() => {
    if (fullIndexActive) return;
    const totalCount = browseData?.totalCount ?? 0;
    const clamped = clampPageToTotal({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      totalCount
    });
    if (clamped !== pagination.pageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: clamped }));
    }
  }, [browseData, pagination.pageIndex, pagination.pageSize, fullIndexActive]);

  const browseRows = React.useMemo(() => browseData?.data ?? [], [browseData]);
  const indexRows = React.useMemo(() => indexData ?? [], [indexData]);

  // Chips narrow the rows BEFORE the grid sees them, so MUI's quick filter
  // (the free-text box) then runs over the already-chipped set. That is what
  // makes the two AND together without either knowing about the other.
  // Once the catalogue has landed, a criterion naming a field it does not
  // contain has no predicate — applyCriteria would drop it and, if it were the
  // only one, hand back EVERY row under a chip claiming otherwise. Dropping it
  // here instead keeps what is displayed and what is applied the same thing.
  // Reachable from a stale or hand-edited link, and from a field withdrawn by
  // a later release.
  React.useEffect(() => {
    // Only prune against a catalogue we actually received. Pruning against an
    // empty one after a failed fetch would delete every chip the user had.
    if (!fieldsReady) return;
    const known = new Set(fields.map((f) => f.key));
    pruneCriteria((c) => known.has(c?.field));
  }, [fieldsReady, fields, pruneCriteria]);

  const sourceRows = fullIndexActive ? indexRows : browseRows;
  // Hold the rows back rather than showing them unfiltered — paired with the
  // loading flag below, so this reads as "still working", not "no matches".
  const criteriaPending =
    criteria.length > 0 && !fieldsReady && !filterFieldsError;
  const activeRows = React.useMemo(
    () => (criteriaPending ? [] : applyCriteria(sourceRows, criteria, fields)),
    [criteriaPending, sourceRows, criteria, fields]
  );

  // What the grid is actually displaying, after its own quick filter has run
  // over the chip-filtered rows. Counting `activeRows` instead reported the
  // pre-text total — "Showing 4,312 people" above seven visible rows.
  // Tagged with the row set it describes, not just stored.
  //
  // The grid publishes a state change before it has taken the rows we just
  // handed it, so the first report for any new search is an EMPTY set. Trusting
  // it unconditionally made the result line read "No matching people" for a
  // frame on every single search — a false negative that self-corrected too
  // fast to debug and too slow to miss. Until the grid has reported on THIS
  // row set, our own count stands.
  const [visible, setVisible] = React.useState({ source: null, rows: null });
  const activeRowsRef = React.useRef(activeRows);
  activeRowsRef.current = activeRows;
  const handleVisibleRows = React.useCallback((rows) => {
    setVisible({ source: activeRowsRef.current, rows });
  }, []);
  const shownRows = visible.source === activeRows ? visible.rows : activeRows;

  // Counts for the result line. `familyCount` keys on String(familyId) — a
  // plain Set of ids would be fine here since they are strings, but keying
  // explicitly is the habit that stops the ObjectId version of this bug.
  const familyCount = React.useMemo(() => {
    const seen = new Set();
    shownRows.forEach((row) => {
      if (row?.familyId) seen.add(String(row.familyId));
    });
    return seen.size;
  }, [shownRows]);

  // "filtered from N" is only honest against the full index; in browse mode
  // the server's own total is the truth.
  const totalBeforeNarrowing = fullIndexActive
    ? indexRows.length
    : browseData?.totalCount ?? 0;

  // When AppContext signals a change (new record added), invalidate the cache.
  // Skip the initial mount so we don't bust the cache on every navigation.
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
    queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
  }, [_proband_users, queryClient]);

  React.useEffect(() => {
    setLoading(browseLoading);
  }, [browseLoading, setLoading]);

  React.useEffect(() => {
    if (browseError || indexError) {
      setSnack({
        status: true,
        msg: t('Error fetching values'),
        severity: 'error'
      });
    }
  }, [browseError, indexError, setSnack]);

  const [mergeSourceRow, setMergeSourceRow] = React.useState(null);
  const [mergeSelectPersons, setMergeSelectPersons] = React.useState(null);
  const [mergeWizardPersons, setMergeWizardPersons] = React.useState(null);

  const hasFocused = React.useRef(false);
  React.useEffect(() => {
    if (!focusSearch || hasFocused.current || !activeRows.length) return;
    hasFocused.current = true;
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, [focusSearch, activeRows]);

  const onViewClick = (e, { user_id, _id }, isFamilyMember = false) => {
    e.stopPropagation();
    if (isFamilyMember) {
      navigate(
        CONSTANTS.familyMemberInfoPath
          .replace(':user_id', user_id)
          .replace(':member_id', _id)
      );
    } else navigate(`${CONSTANTS.probandViewPath}/${_id}`);
  };

  const onFlatPedigreeViewClick = (e, rowData) => {
    e.stopPropagation();
    const probandId = rowData.user_id || rowData._id;
    setLoading(true);
    axios
      .get(APICONSTANTS.getFamiliesInfo.replace(':user_id', probandId))
      .then((res) => {
        setLoading(false);
        const value = { family: res.data, selectedUser: rowData };
        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });
        navigate({
          pathname: CONSTANTS.pedigreeChartPath.replace(
            ':_id',
            rowData.familyId
          ),
          state: { selectedMemberId: rowData._id }
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // onPedigreeViewClick / onViewPatientClick / getXMLData / getJsonData /
  // createUniqueLink / patientView / renderCollapsedTextWithQuery and the
  // UserTableActions import were removed with the dead `columns` memo in
  // TGCLOUD-1818 — that memo was their only consumer, so none of them had been
  // reachable from this grid for some time. No feature is lost: the live
  // actions column offers View and View Pedigree, and XML / GA4-JSON export
  // live on the pedigree sidebar's ExportMenu. NOTE: src/components/proband-info/
  // UserTableActions.jsx is now an orphan with no importer at all.

  // @Dev : the below search funtion is not an appropiate method on seaching as the pedigree config may
  // not be a part of user model; also it may not have evey property of a member

  // const customSearch = (term, rowData) => {
  //   if (!rowData.firstName) rowData.firstName = '';
  //   const currentUser = probandUsers.data.filter((user) => {
  //     return user.currentId === rowData.currentId;
  //   })[0];
  //   let found = false;
  //   if (currentUser.pedigreeConfig?.length > 0) {
  //     for (let x in currentUser.pedigreeConfig) {
  //       let {
  //         firstName = '',
  //         lastName = '',
  //         comments = '',
  //         gender = '',
  //         relationship = ''
  //       } = currentUser.pedigreeConfig[x];
  //       term = term?.toLowerCase();
  //       if (firstName?.toLowerCase().includes(term)) found = true;
  //       if (lastName?.toLowerCase().includes(term)) found = true;
  //       if (comments?.toLowerCase().includes(term)) found = true;
  //       if (gender?.toLowerCase().includes(term)) found = true;
  //       if (relationship?.toLowerCase().includes(term)) found = true;
  //     }
  //   }
  //   return rowData.firstName?.toLowerCase().includes(term) || found;
  // };

  // The ~130-line `columns` memo that stood here was DEAD and was removed in
  // TGCLOUD-1818. It was annotated `no-unused-vars`, never passed to <Table>
  // (the grid has always been fed `flatColumns`), and the `preloadFamilyAgg`
  // fan-out that filled the Family Diseases / Phenotypes / Genotypes cells it
  // defined was never called — so `familyAgg` and `famAggTexts` were
  // permanently `{}` and the per-row getFamiliesInfo requests never happened.
  // Reinstating family-level aggregate columns means reinstating that fetch;
  // the helper it used, `aggregateFamilyAnnotations`, still exists in
  // src/util/util.js.

  const boxColumns = React.useMemo(
    () => [
      {
        header: 'UPN',
        accessorKey: 'upn',
        size: 10,
        sortingFn: 'alphanumeric'
      },
      {
        header: t('Relationship'),
        accessorKey: 'relationship',
        Cell: ({ row }) => relationshipMap[row.original.relationship] ?? ''
      },
      {
        header: t('First Name'),
        accessorKey: 'firstName'
      },
      {
        header: t('Last Name'),
        accessorKey: 'lastName'
      },
      {
        header: t('Date of Birth'),
        accessorKey: 'dob',
        getApplyQuickFilterFn: dateQuickFilter,
        Cell: ({ row }) =>
          renderStoredDate(
            row.original.dob,
            row.original.dobPrecision,
            row.original.estimatedDob,
            fmtDate
          )
      },
      {
        header: t('Date of Death'),
        accessorKey: 'dod',
        getApplyQuickFilterFn: dateQuickFilter,
        Cell: ({ row }) =>
          renderStoredDate(
            row.original.dod,
            row.original.dodPrecision,
            row.original.estimatedDod,
            fmtDate
          )
      },
      {
        header: t('Disease (Age)'),
        id: 'diagnoses',
        accessorFn: (row) => {
          const list = row.diagnoses;
          if (!Array.isArray(list) || list.length === 0) return '';
          return list
            .map((it) => {
              const label = toLabel(it, diagnosesMap);
              const age =
                typeof it === 'string' ? row.diagnosesAge?.[it] : it?.age;
              return label + (age ? ` (${age})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 150,
        Cell: ({ row }) => {
          const list = row.original.diagnoses;
          const ageMap = row.original.diagnosesAge;
          if (!Array.isArray(list) || list.length === 0) return null;
          return list.map((it, i) => {
            const label = toLabel(it, diagnosesMap);
            const age = typeof it === 'string' ? ageMap?.[it] : it?.age;
            const text = label + (age ? ` (${age})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, highlightQuery)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Phenotypes'),
        id: 'phenotypes',
        accessorFn: (row) => {
          const list = row.phenotypes;
          if (!Array.isArray(list) || list.length === 0) return '';
          return list
            .map((p) => {
              const label = toLabel(p);
              const id = typeof p === 'object' ? p.id : '';
              return label + (id && label && id !== label ? ` (${id})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 180,
        Cell: ({ row }) => {
          const list = row.original.phenotypes;
          if (!Array.isArray(list) || list.length === 0) return null;
          return list.map((p, i) => {
            const label = toLabel(p);
            const id = typeof p === 'object' ? p.id : '';
            const text =
              label + (id && label && id !== label ? ` (${id})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, highlightQuery)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Genotypes'),
        id: 'genotypes',
        accessorFn: (row) => {
          const list = displayableGenotypes(row.genotypes);
          if (list.length === 0) return '';
          return list
            .map((g) => {
              const label = toLabel(g);
              const id = typeof g === 'object' ? g.id : '';
              return label + (id && label && id !== label ? ` (${id})` : '');
            })
            .join(' | ');
        },
        enableSorting: false,
        size: 180,
        Cell: ({ row }) => {
          const list = displayableGenotypes(row.original.genotypes);
          if (list.length === 0) return null;
          return list.map((g, i) => {
            const label = toLabel(g);
            const id = typeof g === 'object' ? g.id : '';
            const text =
              label + (id && label && id !== label ? ` (${id})` : '');
            return (
              <Typography variant="body2" key={i}>
                {highlightMatch(text, highlightQuery)}
              </Typography>
            );
          });
        }
      },
      {
        header: t('Tags'),
        id: 'personTags',
        accessorFn: (row) =>
          (row.personTags || []).map(({ tag }) => tag).join(' '),
        enableSorting: false,
        size: 200,
        Cell: ({ row }) => {
          const tags = row.original.personTags;
          if (!Array.isArray(tags) || tags.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {tags.map(({ tag, tagColor }) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  style={resolveTagStyle(tag, tagColor)}
                  sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
              ))}
            </div>
          );
        },
        filterFn: (row, id, filterValue) => {
          const tags = row.original.personTags ?? [];
          const tagsText = tags.map(({ tag }) => tag?.toLowerCase()).join(' ');
          return tagsText.includes(filterValue.toLowerCase());
        }
      },
      {
        header: t('Comments'),
        accessorKey: 'comments',
        enableSorting: false
      },
      {
        header: t('Actions'),
        accessorKey: 'actions',
        enableSorting: false,
        size: 80,
        Cell: ({ row }) => {
          const rowData = row.original;
          return (
            <>
              {rowData.disableEdit ? (
                <CustomIconBtn
                  icon={<Visibility fontSize="small" />}
                  disabled={true}
                />
              ) : (
                <CustomIconBtn
                  type="view"
                  tooltip={t('View Submission')}
                  onClick={(e) => onViewClick(e, rowData, true)}
                  disabled={rowData.disableEdit}
                />
              )}
            </>
          );
        }
      }
    ],
    [t, highlightQuery, dateQuickFilter, fmtDate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const flatColumns = React.useMemo(() => {
    const familyIdCol = {
      accessorKey: 'familyId',
      header: t('Family ID'),
      size: 120
    };
    const memberCols = boxColumns.filter((c) => c.accessorKey !== 'actions');
    const statusCol = {
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      Cell: ({ row }) => (
        <UsersTableStatusCol
          status={row.original.status}
          merged={row.original.merged}
          mergedInto={row.original.mergedInto}
        />
      )
    };
    const actionCol = patient
      ? []
      : [
          {
            header: t('Actions'),
            accessorKey: 'actions',
            enableSorting: false,
            size: 150,
            Cell: ({ row }) => {
              const rowData = row.original;
              const isProband = rowData.upn === 1;
              return (
                <>
                  {rowData.disableEdit ? (
                    <CustomIconBtn
                      icon={<Visibility fontSize="small" />}
                      disabled={true}
                    />
                  ) : (
                    <CustomIconBtn
                      type="view"
                      tooltip={t('View Submission')}
                      onClick={(e) => onViewClick(e, rowData, !isProband)}
                    />
                  )}
                  <CustomIconBtn
                    type="view-pedigree"
                    tooltip={t('View Pedigree')}
                    onClick={(e) => onFlatPedigreeViewClick(e, rowData)}
                  />
                  {false && !rowData.merged && (
                    <CustomIconBtn
                      icon={<MergeTypeIcon fontSize="small" />}
                      tooltip={t('Merge Person')}
                      onClick={() =>
                        setMergeSourceRow({
                          firstName: rowData.firstName,
                          lastName: rowData.lastName,
                          dob: rowData.dob,
                          familyId: rowData.familyId,
                          // Probands carry proBandId; family members carry familyMemberId.
                          familyMemberId:
                            rowData.familyMemberId || rowData.proBandId
                        })
                      }
                    />
                  )}
                </>
              );
            }
          }
        ];
    // A hidden identifier reveals its own column while a criterion targets it.
    // Without this the user filters on a hospital number and gets rows with no
    // visible reason for being there — "why is this row in my results?" is the
    // other half of the problem chips exist to solve. It swaps back out when
    // the criterion goes, so the grid is not permanently widened.
    const revealed = fields
      .filter(
        (f) =>
          f.hidden && criteria.some((criterion) => criterion.field === f.key)
      )
      .map((f) => ({
        accessorKey: f.key,
        header: (f.relabellable && tenantFieldLabels?.[f.key]) || t(f.labelKey),
        size: 140
      }));

    return [familyIdCol, ...memberCols, ...revealed, statusCol, ...actionCol];
  }, [t, boxColumns, patient, fields, criteria, tenantFieldLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enum choices come from the constants the grid already renders with, not
  // from the database: `relationship` is equality-ENCRYPTED on familychangeds,
  // so a $group over it would group by ciphertext and hand back one bucket per
  // row. (Model.distinct is worse still — it is rejected outright on a QE
  // collection.) The static map is both correct and free.
  const optionsFor = React.useCallback(
    (field) => {
      if (field?.key === 'relationship') {
        return Object.entries(relationshipMap).map(([value, label]) => ({
          value,
          // Through t(), like every other user-facing string on this screen —
          // these are the same English phrases the Relationship column renders.
          label: t(label)
        }));
      }
      if (field?.key === 'status') {
        return Object.values(CONSTANTS.user_status ?? {}).map((value) => ({
          value,
          label: t(capitalizeLetter(String(value)))
        }));
      }
      return [];
    },
    [t]
  );

  // Chip VALUES resolve through the same map, translated, so the chip and the
  // grid cell read identically.
  const translatedRelationshipMap = React.useMemo(() => {
    const out = {};
    Object.entries(relationshipMap).forEach(([value, label]) => {
      out[value] = t(label);
    });
    return out;
  }, [t]);

  const valueLabelsFor = React.useCallback(
    (field) =>
      field?.key === 'relationship' ? translatedRelationshipMap : undefined,
    [translatedRelationshipMap]
  );

  // TGCLOUD-1818 Phase 4 — recognise an identifier typed into the search box
  // and offer the equivalent chip. An OFFER: Enter still does exactly what it
  // always did, and ignoring this costs nothing.
  const suggestion = React.useMemo(() => {
    const match = suggestIdentifierCriterion(globalFilter, fields);
    if (!match) return null;
    const field = fields.find((f) => f.key === match.field);
    return {
      criterion: {
        field: match.field,
        operator: match.operator,
        value: match.value
      },
      label: criterionSentence(
        { operator: match.operator, value: match.value },
        field,
        t,
        { tenantLabels: tenantFieldLabels }
      )
    };
  }, [globalFilter, fields, t, tenantFieldLabels]);

  const acceptSuggestion = React.useCallback(() => {
    if (!suggestion) return;
    addCriterion(suggestion.criterion);
    // Clearing the box is what makes this a CONVERSION rather than a second,
    // redundant narrowing: the term has become a criterion, so leaving it in
    // the box would AND the same value against every column as well.
    setGlobalFilter('');
  }, [suggestion, addCriterion, setGlobalFilter]);

  // CLINICIAN ONLY. UsersTableInfo is mounted twice — here and on the patient
  // portal's "Your Family History" (PatientProband.jsx), where `patient` is
  // true. A patient sees one family, so a criteria builder has nothing to
  // narrow; the free-text box stays for both.
  //
  // Memoised because `probandTable` depends on it: an element rebuilt every
  // render would defeat that memo and re-render a grid holding the whole
  // tenant on every keystroke.
  const filterBar = React.useMemo(
    () =>
      patient ? null : (
        <FamilySearchFilterBar
          criteria={criteria}
          fields={fields}
          onAdd={addCriterion}
          onUpdate={updateCriterion}
          onRemove={removeCriterion}
          optionsFor={optionsFor}
          valueLabelsFor={valueLabelsFor}
          tenantLabels={tenantFieldLabels}
          suggestion={suggestion}
          onAcceptSuggestion={acceptSuggestion}
          t={t}
        />
      ),
    [
      patient,
      criteria,
      fields,
      addCriterion,
      updateCriterion,
      removeCriterion,
      optionsFor,
      valueLabelsFor,
      tenantFieldLabels,
      suggestion,
      acceptSuggestion,
      t
    ]
  );

  const probandTable = React.useMemo(() => {
    const paginationProps = fullIndexActive
      ? {
          // The parent's own page size, not a literal. Table mirrors props on
          // to its internal model only when the PROP value changes, so a
          // hardcoded 15 here meant that flipping back to browse could leave
          // the grid's internal page size at whatever the user had chosen
          // while searching, under a footer computed from a different number.
          // Passing the value the parent already believes keeps the two in
          // step, and carries the user's chosen page size into search mode
          // rather than silently resetting it. (Pre-dates TGCLOUD-1818; chips
          // and sorting make this mode far easier to reach.)
          pageSize: pagination.pageSize,
          page: 0,
          state: { isLoading: indexFetching || !indexData || criteriaPending }
        }
      : {
          pageSize: pagination.pageSize,
          page: pagination.pageIndex,
          totalCount: browseData?.totalCount ?? 0,
          // Table (DataGrid) hands back a resolved { page, pageSize } model;
          // map it onto our internal { pageIndex, pageSize } shape.
          onPaginationChange: (model) =>
            setPagination({
              pageIndex: model.page,
              pageSize: model.pageSize
            }),
          // isFetching, not isLoading: with placeholderData the previous page
          // stays on screen and isLoading is false from the second page onward,
          // so isLoading alone would show no feedback at all while paging.
          state: { isLoading: browseFetching }
        };

    return (
      <Table
        title="Family Members"
        rows={activeRows}
        pageSizeOptions={[5, 10, 15, 20, 30, 40, 50]}
        columns={flatColumns}
        searchInputRef={searchInputRef}
        // This screen's own wording, per the TGCLOUD-1818 mock-up. Passed
        // rather than changed in Table.jsx so the other 21 tables keep theirs.
        searchPlaceholder={t('Search all fields')}
        tableKey={TABLE_CONSTANTS.familyMembers}
        columnPinning={{ right: ['actions'] }}
        globalFilter={globalFilter}
        onGlobalFilterChange={(v) => setGlobalFilter(v ?? '')}
        onSortModelChange={(model) => setSortActive((model?.length ?? 0) > 0)}
        onVisibleRowsChange={handleVisibleRows}
        filterBar={filterBar}
        {...paginationProps}
      />
    );
  }, [
    fullIndexActive,
    filterBar,
    setGlobalFilter,
    t,
    activeRows,
    flatColumns,
    focusSearch,
    globalFilter,
    pagination,
    browseData,
    browseLoading,
    browseFetching,
    indexFetching,
    indexData
  ]);

  return (
    <>
      {probandTable}
      {!patient && (
        <FamilySearchResultLine
          shownCount={shownRows.length}
          totalCount={totalBeforeNarrowing}
          familyCount={familyCount}
          criteriaCount={criteria.length}
          hasSearchText={searchActive}
          narrowed={criteria.length > 0 || searchActive}
          // The catalogue race is the SHORT wait; the whole-tenant index is the
          // long one. Reporting "No matching people" for the several seconds
          // that download takes is a false negative in words, and it is the
          // reading that makes someone stop looking for a relative.
          pending={
            criteriaPending ||
            (fullIndexActive && (indexFetching || !indexData))
          }
          unavailable={filtersUnavailable}
          onClearAll={clearAll}
          t={t}
        />
      )}
      {mergeSourceRow && (
        <PersonSearchPicker
          open={!!mergeSourceRow}
          onClose={() => setMergeSourceRow(null)}
          onSelect={(selectedPerson) => {
            setMergeSelectPersons({
              personA: mergeSourceRow,
              personB: selectedPerson
            });
            setMergeSourceRow(null);
          }}
          excludeFamilyMemberId={mergeSourceRow.familyMemberId}
        />
      )}
      {mergeSelectPersons && (
        <MergePersonSelectModal
          open={!!mergeSelectPersons}
          personA={mergeSelectPersons.personA}
          personB={mergeSelectPersons.personB}
          onClose={() => setMergeSelectPersons(null)}
          onSelect={({ retained, deleted }) => {
            setMergeSelectPersons(null);
            setMergeWizardPersons({ retained, deleted });
          }}
        />
      )}
      {mergeWizardPersons && (
        <MergePersonWizard
          open={!!mergeWizardPersons}
          retained={mergeWizardPersons.retained}
          deleted={mergeWizardPersons.deleted}
          onClose={() => setMergeWizardPersons(null)}
          onSuccess={() => {
            setMergeWizardPersons(null);
            queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
            queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
          }}
        />
      )}
    </>
  );
}
